import { useEffect, useRef } from 'react';
import { BrowserRouter, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { GlobalStyle } from './Globals';
import { themes } from './theme';
import { useStore } from './stores/store';
import { cacheRead } from './api/client';
import { Layout } from './components/Layout';
import { AppRoutes } from './router';
import { Welcome } from './components/Welcome';
import { Announcements } from './components/Announcements';
import { sessionStart, identify, flagAdmin, pageEnter, flush, resume } from './lib/analytics';
import { setTz } from './lib/format';

// Oscurece un color hex (hacia el negro) para el extremo del degradé.
function darken(hex: string, f = 0.55): string {
  const m = hex.replace('#', '');
  const n = m.length === 3 ? m.split('').map((c) => c + c).join('') : m;
  const ch = (i: number) => Math.round(parseInt(n.slice(i, i + 2), 16) * (1 - f));
  return '#' + [ch(0), ch(2), ch(4)].map((x) => x.toString(16).padStart(2, '0')).join('');
}

// Inicio en "Hoy". Cuando el Mundial termina (remaining 0), el inicio pasa a "Resumen".
function RouteTracker() {
  const loc = useLocation();
  const navigate = useNavigate();
  const started = useRef(false);

  useEffect(() => {
    if (loc.pathname === '/') {
      const t = cacheRead<{ remaining: number }>('/tournament');
      if (t != null && t.remaining === 0) navigate('/resumen', { replace: true });
    }
    started.current = true;
  }, []);

  return null;
}

// Analítica anónima: registra ingreso, páginas visitadas y tiempo en cada una.
function AnalyticsTracker() {
  const loc = useLocation();
  const nick = useStore((s) => s.nick);
  const adminKey = useStore((s) => s.adminKey);
  const firstNick = useRef(true);
  const firstAdmin = useRef(true);

  useEffect(() => {
    const st = useStore.getState();
    sessionStart(st.nick, !!st.adminKey);
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
      else resume(); // volvió a primer plano: no contar el tiempo en background
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flush);
    };
  }, []);

  useEffect(() => {
    pageEnter(loc.pathname);
  }, [loc.pathname]);

  // Cuando se elige el nick (en el aviso de bienvenida), lo asocia a la pubkey.
  useEffect(() => {
    if (firstNick.current) {
      firstNick.current = false;
      return; // el valor inicial ya viaja en sessionStart
    }
    if (nick) identify(nick);
  }, [nick]);

  // Cuando desbloquea /uso, marca su pubkey como admin para excluirla de las métricas.
  useEffect(() => {
    if (firstAdmin.current) {
      firstAdmin.current = false;
      return; // el valor inicial ya viajó en sessionStart
    }
    if (adminKey) flagAdmin();
  }, [adminKey]);

  return null;
}

export function App() {
  const themeName = useStore((s) => s.theme);
  const accent = useStore((s) => s.accent);
  const tz = useStore((s) => s.tz);
  setTz(tz); // las funciones de fecha/hora usan esta zona
  const base = themes[themeName];
  const theme = accent
    ? { ...base, colors: { ...base.colors, accent, accentDark: darken(accent) } }
    : base;
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <Welcome />
      <Announcements />
      <BrowserRouter basename={process.env.PUBLIC_URL || undefined}>
        <RouteTracker />
        <AnalyticsTracker />
        <Layout>
          <AppRoutes />
        </Layout>
      </BrowserRouter>
    </ThemeProvider>
  );
}
